import React, { createContext, useContext, useEffect, useState } from "react";
import { getItem, removeItem, setItem } from "../utils/utils";
import { MenuItem, menuItems } from "../utils/constants";
import { commonStrings } from "../static/commonStrings";

export interface Permission {
  module: string;
  actions: string[];
}

export interface User {
  userId: number;
  fullName: string;
  mobileNo: string;
  email: string;
  rankName: string;
  rankId: number;
  policeStation: string;
  zoneId: number;
  location: string;
  dateOfBirth: string;
  roleName: string | null;
  roleId: number;
  status: string;
  emailConfirmed: boolean;
  permissions: Permission[];
  assemblyConstituencyId: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: { user: User; token: string }) => void;
  logout: () => Promise<void>;
  filteredMenuItems: MenuItem[];
  hasPermission: (module: string, action?: string) => boolean;
  updateUser: (updatedUserData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const menuItemModuleMap: Record<string, string> = {
    Dashboard: "Dashboard",
    Roles: "Roles",
    Users: "Users",
    Groups: "Group",
    Events: "EventTags",
    Ranks: "Users",
    Messages: "Dashboard",
    RIO: commonStrings.sideBarMenuTitles.rio,
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = getItem("token");
      const storedUser = getItem("user");

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (userData: { user: User; token: string }) => {
    setUser(userData.user);
    setToken(userData.token);
    setItem({ key: "token", value: userData.token });
    setItem({ key: "user", value: JSON.stringify(userData.user) });
  };

  const logout = async () => {
    try {
      console.warn("In logout handler");
      removeItem("token");
      removeItem("user");
      removeItem("showRoleAlertInfo");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    } finally {
      setUser(null);
      setToken(null);
    }
  };

  const updateUser = (updatedUserData: Partial<User>) => {
    if (!user) return;
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    setItem({ key: "user", value: JSON.stringify(updatedUser) });
  };

  const hasPermission = (module: string, action?: string): boolean => {
    if (!user) return false;

    const permission = user.permissions.find((p) => p.module === module);

    if (!permission) return false;
    if (!action) return true;

    return permission.actions.includes(action);
  };

  const filteredMenuItems = menuItems.filter((item) => {
    const module = menuItemModuleMap[item.text];
    if (!module) return false;

    if (
      module === commonStrings.sideBarMenuTitles.assemblyConstituency ||
      module === commonStrings.sideBarMenuTitles.rio
    ) {
      return hasPermission("Users", "View");
    }

    if (module === "Dashboard") {
      return hasPermission("Dashboard", "View");
    }

    return hasPermission(module, "View");
  });

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    logout,
    filteredMenuItems,
    hasPermission,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
